import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { UserPlus } from 'lucide-react'
import { useAuth } from '../../core/auth'
import {
  ROLES, ROLE_TOOLS, TOOL_LABELS, SCOPES, SCOPE_LABELS,
  administrableScopes, canGrant, grantString, grantsToRemove, holdsRole, roleLabels,
} from '../../core/caps'
import type { Scope } from '../../core/types'
import { useUsers, setCaps, setActive, seenLabel, toDate, type UserRow } from './model'
import { AddPerson, PersonEditor, ScopeSelect, firstScope, summarise, type Actor } from './Person'
import { RosterTab } from './RosterTab'
import { Loading, Empty, ErrorState, Denied } from '../../ui/States'

/**
 * /admin/accounts — every staff account in the district, for whoever hands out
 * access. A district admin sees and can change everyone; a building admin sees
 * everyone (the rules let anyone holding admin.grant list accounts) but can
 * only change what they hold, in the scopes they administer.
 *
 * A desk job, so it is a sortable table above the breakpoint and cards below.
 * Bulk actions live on the table only — selecting forty rows is not something
 * anybody does with a thumb.
 *
 * Students get their own tab when student sign-in lands; their source of truth
 * is the roster Sheet, so that tab will be mostly read-only.
 */

type Status = 'any' | 'active' | 'off' | 'never'
type SortKey = 'name' | 'seen'

export function Accounts() {
  const { profile, user, can } = useAuth()
  const mayGrant = can('admin.grant')
  const { rows, err } = useUsers(mayGrant)
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('person')
  const view = params.get('view') === 'students' ? 'students' : 'staff'
  const [adding, setAdding] = useState(false)

  const [q, setQ] = useState('')
  const [building, setBuilding] = useState<Scope | 'any'>('any')
  const [role, setRole] = useState('any')
  const [status, setStatus] = useState<Status>('active')
  const [sort, setSort] = useState<SortKey>('name')
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const scopes = useMemo(() => administrableScopes(profile ?? {}), [profile])
  const who = profile?.displayName ?? user?.email ?? 'unknown'
  const actor: Actor = {
    email: (user?.email ?? '').toLowerCase(),
    caps: profile?.caps, districtAdmin: profile?.districtAdmin,
    mayEditDirectory: can('admin.staff'),
  }
  const open = (id: string | null) => setParams(id ? { person: id } : {})
  const setView = (v: 'staff' | 'students') => setParams(v === 'students' ? { view: v } : {})

  const shown = useMemo(() => (rows ?? [])
    .filter((r) => !q || (r.displayName + ' ' + r.email).toLowerCase().includes(q.toLowerCase()))
    .filter((r) => building === 'any' || (r.buildings ?? []).includes(building))
    .filter((r) => matchesRole(r, role))
    .filter((r) => status === 'any'
      || (status === 'active' && r.active)
      || (status === 'off' && !r.active)
      || (status === 'never' && !toDate(r.lastSeenAt)))
    .sort((a, b) => sort === 'name'
      ? a.displayName.localeCompare(b.displayName)
      : (toDate(a.lastSeenAt)?.getTime() ?? 0) - (toDate(b.lastSeenAt)?.getTime() ?? 0)),
  [rows, q, building, role, status, sort])

  if (!mayGrant) return <Denied what="accounts" />
  if (err) return <ErrorState body={`Could not load accounts. ${err}`} />
  if (!rows) return <Loading label="Loading accounts" />

  const person = rows.find((r) => r.id === selectedId)
  if (person) return <PersonEditor person={person} scopes={scopes} actor={actor} who={who} onBack={() => open(null)} />
  if (adding) {
    return <AddPerson scopes={scopes} actor={actor} who={who} existing={rows}
      onDone={(id) => { setAdding(false); if (id) open(id) }} />
  }

  const never = rows.filter((r) => r.active && !toDate(r.lastSeenAt)).length
  const pickable = shown.filter((r) => r.id !== actor.email && !r.districtAdmin)
  const allPicked = pickable.length > 0 && pickable.every((r) => picked.has(r.id))
  const togglePick = (id: string) => setPicked((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n
  })

  const tabs = (
    <div className="chips" role="tablist" style={{ margin: '0 0 var(--s-4)' }}>
      {(['staff', 'students'] as const).map((v) => (
        <button key={v} role="tab" aria-selected={view === v} className={`chip${view === v ? ' is-on' : ''}`}
          onClick={() => setView(v)}>{v === 'staff' ? 'Staff' : 'Students'}</button>
      ))}
    </div>
  )

  if (view === 'students') {
    return (
      <div className="wide">
        <h1 className="page-title" style={{ margin: '0 0 var(--s-3)' }}>Accounts</h1>
        {tabs}
        <RosterTab canEdit={can('norseterm.run') || can('norseterm.attend')} />
      </div>
    )
  }

  return (
    <div className="wide">
      <div className="pagehead">
        <div>
          <h1 className="page-title" style={{ margin: '0 0 var(--s-1)' }}>Accounts</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-4)', lineHeight: 1.5 }}>
            {rows.length} staff accounts · {rows.filter((r) => !r.active).length} turned off
            {never > 0 && <> · <button className="linkish" onClick={() => setStatus('never')}>{never} never signed in</button></>}
            . You can change {profile?.districtAdmin ? 'anyone' : `what you hold, in ${scopes.map((s) => SCOPE_LABELS[s]).join(', ') || 'no scopes yet'}`}.
          </p>
        </div>
        <button className="btn" style={{ width: 'auto', flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setAdding(true)}>
          <UserPlus size={16} aria-hidden="true" /> Add someone
        </button>
      </div>

      {tabs}

      <div className="filters">
        <input className="opt__input" placeholder="Search name or email" value={q}
          aria-label="Search name or email" onChange={(e) => setQ(e.target.value)} />
        <select className="opt__input" aria-label="Building" value={building}
          onChange={(e) => setBuilding(e.target.value as Scope | 'any')}>
          <option value="any">Every building</option>
          {SCOPES.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
        </select>
        <select className="opt__input" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="any">Any role</option>
          <option value="district-admin">District admin</option>
          {ROLES.map((r) => <option key={r.id} value={r.id}>{TOOL_LABELS[r.tool]} · {r.label}</option>)}
          <option value="none">No tools</option>
        </select>
        <select className="opt__input" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
          <option value="active">Active</option>
          <option value="off">Turned off</option>
          <option value="never">Never signed in</option>
          <option value="any">Everyone</option>
        </select>
      </div>

      {picked.size > 0 && (
        <BulkBar
          people={rows.filter((r) => picked.has(r.id))}
          scopes={scopes} actor={actor} who={who}
          onDone={() => setPicked(new Set())} />
      )}

      {shown.length === 0 ? (
        <Empty title="Nobody matches" body="Try a different filter, or clear the search." />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox" aria-label="Select everyone shown" checked={allPicked}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(pickable.map((r) => r.id)))} />
                </th>
                <th><button className="th-sort" onClick={() => setSort('name')} aria-pressed={sort === 'name'}>Name</button></th>
                <th>Buildings</th>
                {ROLE_TOOLS.map((t) => <th key={t}>{TOOL_LABELS[t] ?? t}</th>)}
                <th><button className="th-sort" onClick={() => setSort('seen')} aria-pressed={sort === 'seen'}>Last sign-in</button></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const mayPick = r.id !== actor.email && !r.districtAdmin
                return (
                  <tr key={r.id} className={r.active ? '' : 'is-off'}>
                    <td>
                      {mayPick && <input type="checkbox" aria-label={`Select ${r.displayName}`}
                        checked={picked.has(r.id)} onChange={() => togglePick(r.id)} />}
                    </td>
                    <td>
                      <button className="row-link" onClick={() => open(r.id)}>{r.displayName}</button>
                      <div className="work__sub">{r.email}</div>
                      {!r.active && <span className="pill pill--serious" style={{ marginTop: 4 }}>Turned off</span>}
                    </td>
                    <td>{(r.buildings ?? []).map((b) => SCOPE_LABELS[b]).join(', ') || '—'}</td>
                    {ROLE_TOOLS.map((t) => <td key={t}>{roleLabels(r, t).join(', ') || '—'}</td>)}
                    <td className={toDate(r.lastSeenAt) ? '' : 'is-muted'}>{seenLabel(r.lastSeenAt)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.id} className="work__row">
                <div className="work__main">
                  <button className="work__title" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    onClick={() => open(r.id)}>
                    {r.displayName}
                  </button>
                  <div className="work__sub">{r.email}</div>
                  <div className="work__status">{summarise(r)}</div>
                  <div className="work__sub">Last signed in: {seenLabel(r.lastSeenAt)}</div>
                </div>
                {!r.active && <span className="pill pill--serious">Off</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function matchesRole(r: UserRow, role: string): boolean {
  if (role === 'any') return true
  if (role === 'district-admin') return !!r.districtAdmin
  if (role === 'none') return !r.districtAdmin && !(r.caps ?? []).length
  const def = ROLES.find((x) => x.id === role)
  return !!def && !r.districtAdmin && holdsRole(r.caps, def)
}

/**
 * Do one thing to everyone selected. Each person is a separate write, checked
 * by the rules on its own — so a bulk action can never do anything the admin
 * could not have done one row at a time, and the result says plainly who was
 * skipped and why rather than failing the lot.
 */
function BulkBar({ people, scopes, actor, who, onDone }: {
  people: UserRow[]
  scopes: Scope[]
  actor: Actor
  who: string
  onDone: () => void
}) {
  const [scope, setScope] = useState<Scope>(firstScope(scopes))
  const roles = ROLES.filter((r) => r.caps.every((c) => canGrant(actor, c, scope)))
  const [roleId, setRoleId] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const apply = async (what: 'give' | 'take' | 'off' | 'on') => {
    setBusy(true); setResult(null)
    const role = ROLES.find((r) => r.id === roleId)
    let done = 0
    const failed: string[] = []
    for (const p of people) {
      try {
        if (what === 'off' || what === 'on') {
          if (p.active === (what === 'on')) continue
          await setActive(p.id, what === 'on', who)
        } else if (role) {
          const grants = role.caps.map((c) => grantString(c, scope))
          const held = p.caps ?? []
          const next = what === 'give'
            ? [...held, ...grants.filter((g) => !held.includes(g))]
            : held.filter((g) => !grantsToRemove(held, role, scope).includes(g))
          if (next.length === held.length && next.every((g, i) => g === held[i])) continue
          await setCaps(p.id, next, who)
        }
        done++
      } catch { failed.push(p.displayName) }
    }
    setBusy(false)
    setResult(`Changed ${done} ${done === 1 ? 'account' : 'accounts'}.`
      + (failed.length ? ` Not changed. The rules refused: ${failed.join(', ')}.` : ''))
  }

  return (
    <div className="bulkbar" role="region" aria-label="Bulk actions">
      <strong>{people.length} selected</strong>
      <ScopeSelect scopes={scopes} scope={scope} setScope={setScope} />
      <select className="opt__input" aria-label="Role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
        <option value="">Choose a role…</option>
        {roles.map((r) => <option key={r.id} value={r.id}>{TOOL_LABELS[r.tool]} · {r.label}</option>)}
      </select>
      <button className="btn" disabled={busy || !roleId} onClick={() => apply('give')}>Give role</button>
      <button className="btn btn--ghost" disabled={busy || !roleId} onClick={() => apply('take')}>Take role away</button>
      <button className="btn btn--ghost" disabled={busy} onClick={() => apply('off')}>Turn off</button>
      <button className="btn btn--ghost" disabled={busy} onClick={() => apply('on')}>Turn on</button>
      <button className="linkish" disabled={busy} onClick={onDone}>Clear selection</button>
      {result && <p className="opt__hint" role="status" style={{ flexBasis: '100%', margin: 0 }}>{result}</p>}
    </div>
  )
}
